import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import ProsecutorApi from '../../api/ProsecutorApi';

interface Option {
  Prosecutor_guid: string;
  Prosecutor_Name: string;
  Prosecutor_Email: string;
  courtName: string | null;
}

/**
 * Put a prosecutor who is already on file onto this court as well (Lloyd, 8 Oct 2026: Sara Kerr at
 * Seguin, Bartlett, Buckholts, Lago Vista and Leon Valley). "Add Prosecutor" creates a new person;
 * using it for somebody who works several courts made a copy per court, each edited on its own.
 */
const AddExistingProsecutor: React.FC<{
  courtGuid: string;
  /** Already on this court, so not offered again. */
  excludeGuids: string[];
  onAdded: () => void;
}> = ({ courtGuid, excludeGuids, onAdded }) => {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<Option[]>([]);
  const [choice, setChoice] = useState<Option | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A string, not the array: the caller builds a new array on every render, and depending on that
  // would re-run the search after each one.
  const excludeKey = excludeGuids.join(',');

  useEffect(() => {
    const excluded = excludeKey.split(',');
    const term = input.trim();
    if (term.length < 2) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      ProsecutorApi.searchProsecutors({ search: term, size: 20 })
        .then((response) => {
          if (cancelled) return;
          setOptions(
            response.prosecutors
              .filter((p) => !excluded.includes(p.Prosecutor_guid))
              .map((p) => ({
                Prosecutor_guid: p.Prosecutor_guid,
                Prosecutor_Name: p.Prosecutor_Name,
                Prosecutor_Email: p.Prosecutor_Email,
                courtName: p.court?.Court_Name ?? null,
              })),
          );
        })
        .catch(() => !cancelled && setOptions([]))
        .finally(() => !cancelled && setLoading(false));
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [input, excludeKey]);

  const add = async () => {
    if (!choice) return;
    try {
      setSaving(true);
      setError(null);
      await ProsecutorApi.addToCourt(choice.Prosecutor_guid, courtGuid);
      setChoice(null);
      setInput('');
      onAdded();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add the prosecutor.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box mb={2}>
      <Box display="flex" gap={1} alignItems="center">
        <Autocomplete
          size="small"
          sx={{ flex: 1, minWidth: 260 }}
          options={options}
          value={choice}
          onChange={(_, value) => setChoice(value)}
          inputValue={input}
          onInputChange={(_, value) => setInput(value)}
          filterOptions={(x) => x}
          getOptionLabel={(o) => o.Prosecutor_Name}
          isOptionEqualToValue={(a, b) => a.Prosecutor_guid === b.Prosecutor_guid}
          loading={loading}
          noOptionsText={input.trim().length < 2 ? 'Type a name or email' : 'No match on file'}
          renderOption={(props, o) => (
            <li {...props} key={o.Prosecutor_guid}>
              <Box>
                <Typography variant="body2">{o.Prosecutor_Name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {o.Prosecutor_Email}
                  {o.courtName ? ` · ${o.courtName}` : ''}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Add a prosecutor already on file"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          disabled={saving}
        />
        <Button size="small" variant="outlined" disabled={!choice || saving} onClick={add}>
          Add to this court
        </Button>
      </Box>
      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default AddExistingProsecutor;
